import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import {
  extractErrorMessage,
  extractSuccessMessage,
} from '../utils/error-parser.util';

export interface FeedbackOptions {
  summary?: string;
  detail?: string;
  life?: number;
  sticky?: boolean;
  closable?: boolean;
  key?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private readonly messageService = inject(MessageService);

  /** Guarda histórico recente de mensagens exibidas para evitar duplicados simultâneos */
  private readonly recentMessages = new Map<string, number>();
  private readonly DEDUPLICATION_WINDOW_MS = 1800;

  /**
   * Exibe notificação de sucesso
   */
  showSuccess(
    messageOrResponse: unknown,
    options?: FeedbackOptions | string,
  ): void {
    const summary =
      typeof options === 'string' ? options : (options?.summary ?? 'Sucesso');
    const customDetail =
      typeof options === 'object' ? options?.detail : undefined;
    const life = typeof options === 'object' ? (options?.life ?? 3500) : 3500;

    const detail =
      customDetail ||
      extractSuccessMessage(
        messageOrResponse,
        'Operação realizada com sucesso.',
      );

    this.addToast({
      severity: 'success',
      summary,
      detail,
      life,
      closable:
        typeof options === 'object' ? (options?.closable ?? true) : true,
      sticky: typeof options === 'object' ? (options?.sticky ?? false) : false,
      key: typeof options === 'object' ? options?.key : undefined,
    });
  }

  /**
   * Exibe notificação de erro, extraindo com precisão a mensagem do backend
   */
  showError(errorOrMessage: unknown, options?: FeedbackOptions | string): void {
    const summary =
      typeof options === 'string' ? options : (options?.summary ?? 'Erro');
    const customDetail =
      typeof options === 'object' ? options?.detail : undefined;
    const life = typeof options === 'object' ? (options?.life ?? 5000) : 5000;

    const detail =
      customDetail ||
      extractErrorMessage(
        errorOrMessage,
        'Ocorreu um erro inesperado. Tente novamente.',
      );

    this.addToast({
      severity: 'error',
      summary,
      detail,
      life,
      closable:
        typeof options === 'object' ? (options?.closable ?? true) : true,
      sticky: typeof options === 'object' ? (options?.sticky ?? false) : false,
      key: typeof options === 'object' ? options?.key : undefined,
    });
  }

  /**
   * Exibe notificação de aviso / alerta
   */
  showWarning(message: string, options?: FeedbackOptions | string): void {
    const summary =
      typeof options === 'string' ? options : (options?.summary ?? 'Atenção');
    const customDetail =
      typeof options === 'object' ? options?.detail : undefined;
    const life = typeof options === 'object' ? (options?.life ?? 4500) : 4500;

    const detail = customDetail || message;

    this.addToast({
      severity: 'warn',
      summary,
      detail,
      life,
      closable:
        typeof options === 'object' ? (options?.closable ?? true) : true,
      sticky: typeof options === 'object' ? (options?.sticky ?? false) : false,
      key: typeof options === 'object' ? options?.key : undefined,
    });
  }

  /**
   * Exibe notificação de informação
   */
  showInfo(message: string, options?: FeedbackOptions | string): void {
    const summary =
      typeof options === 'string'
        ? options
        : (options?.summary ?? 'Informação');
    const customDetail =
      typeof options === 'object' ? options?.detail : undefined;
    const life = typeof options === 'object' ? (options?.life ?? 3500) : 3500;

    const detail = customDetail || message;

    this.addToast({
      severity: 'info',
      summary,
      detail,
      life,
      closable:
        typeof options === 'object' ? (options?.closable ?? true) : true,
      sticky: typeof options === 'object' ? (options?.sticky ?? false) : false,
      key: typeof options === 'object' ? options?.key : undefined,
    });
  }

  /**
   * Limpa todas as mensagens ativas
   */
  clear(key?: string): void {
    this.messageService.clear(key);
    this.recentMessages.clear();
  }

  /**
   * Utilitário para componentes extraírem a mensagem sem disparar toast imediatamente
   */
  extractErrorMessage(err: unknown, fallback?: string): string {
    return extractErrorMessage(err, fallback);
  }

  /**
   * Utilitário para componentes extraírem mensagem de sucesso sem disparar toast imediatamente
   */
  extractSuccessMessage(res: unknown, fallback?: string): string {
    return extractSuccessMessage(res, fallback);
  }

  private addToast(toast: {
    severity: 'success' | 'error' | 'warn' | 'info';
    summary: string;
    detail: string;
    life: number;
    closable?: boolean;
    sticky?: boolean;
    key?: string;
  }): void {
    const key = `${toast.severity}:${toast.detail}`;
    const now = Date.now();
    const lastShown = this.recentMessages.get(key);

    if (lastShown && now - lastShown < this.DEDUPLICATION_WINDOW_MS) {
      // Ignorar toast idêntico recente para evitar duplicados
      return;
    }

    this.recentMessages.set(key, now);

    // Limpeza periódica do histórico de deduplicação
    if (this.recentMessages.size > 50) {
      for (const [k, time] of this.recentMessages.entries()) {
        if (now - time > this.DEDUPLICATION_WINDOW_MS * 2) {
          this.recentMessages.delete(k);
        }
      }
    }

    this.messageService.add(toast);
  }
}
